import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Loader2, Upload, FileText, Download, Table, FileUp } from 'lucide-react';
import { admissionsService } from '../../../firebase/admissions';
import { useRealtime } from '../../../contexts/RealtimeContext';
import toast from 'react-hot-toast';

const FeeStructureManagement = () => {
  const { userProfile } = useRealtime();
  const [feeStructure, setFeeStructure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [inputMode, setInputMode] = useState('table'); // 'table' or 'pdf'
  const [showForm, setShowForm] = useState(false);
  const [tableEntries, setTableEntries] = useState([
    { course: '', fee: '', duration: '', description: '' }
  ]);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfFileName, setPdfFileName] = useState('');

  useEffect(() => {
    loadFeeStructure();
  }, []);

  const loadFeeStructure = async () => {
    try {
      setLoading(true);
      const institutionId = userProfile?.coachingId || userProfile?.institutionId;
      if (!institutionId) {
        setLoading(false);
        return;
      }

      const result = await admissionsService.getInstitutionFeeStructure(institutionId);
      if (result.success && result.feeStructure) {
        const data = result.feeStructure;
        setFeeStructure(data);
        
        if (data.pdfUrl) {
          setInputMode('pdf');
          setPdfUrl(data.pdfUrl);
          setPdfFileName(data.pdfFileName || 'Fee Structure PDF');
        } else if (data.entries && data.entries.length > 0) {
          setInputMode('table');
          setTableEntries(data.entries);
        }
      }
    } catch (error) {
      toast.error('Error loading fee structure');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRow = () => {
    setTableEntries([...tableEntries, { course: '', fee: '', duration: '', description: '' }]);
  };

  const handleRemoveRow = (index) => {
    if (tableEntries.length > 1) {
      setTableEntries(tableEntries.filter((_, i) => i !== index));
    }
  };

  const handleTableEntryChange = (index, field, value) => {
    const updated = [...tableEntries];
    updated[index][field] = value;
    setTableEntries(updated);
  };

  const handlePdfUpload = async (file) => {
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size must be less than 10MB');
      return;
    }

    try {
      setUploading(true);
      const institutionId = userProfile?.coachingId || userProfile?.institutionId;
      const result = await admissionsService.uploadFeeStructurePDF(file, institutionId);

      if (result.success) {
        setPdfUrl(result.url);
        setPdfFileName(file.name);
        toast.success('PDF uploaded successfully');
      } else {
        toast.error('Failed to upload PDF: ' + result.error);
      }
    } catch (error) {
      toast.error('Error uploading PDF: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const institutionId = userProfile?.coachingId || userProfile?.institutionId;
      
      let data = {};
      
      if (inputMode === 'pdf') {
        if (!pdfUrl) {
          toast.error('Please upload a PDF file');
          setSaving(false);
          return;
        }
        data = {
          institutionId,
          pdfUrl,
          pdfFileName,
          entries: null,
          mode: 'pdf'
        };
      } else {
        // Validate table entries
        const validEntries = tableEntries.filter(entry => 
          entry.course.trim() && entry.fee.trim()
        );
        
        if (validEntries.length === 0) {
          toast.error('Please add at least one fee entry with course and fee');
          setSaving(false);
          return;
        }
        
        data = {
          institutionId,
          entries: validEntries,
          pdfUrl: null,
          pdfFileName: null,
          mode: 'table'
        };
      }

      const result = await admissionsService.saveFeeStructure(data);

      if (result.success) {
        toast.success('Fee structure saved successfully');
        setShowForm(false);
        loadFeeStructure();
      } else {
        toast.error('Failed to save: ' + result.error);
      }
    } catch (error) {
      toast.error('Error saving fee structure: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the fee structure?')) {
      return;
    }

    try {
      const institutionId = userProfile?.coachingId || userProfile?.institutionId;
      const result = await admissionsService.deleteFeeStructure(institutionId);
      if (result.success) {
        toast.success('Fee structure deleted successfully');
        setFeeStructure(null);
        setTableEntries([{ course: '', fee: '', duration: '', description: '' }]);
        setPdfUrl('');
        setPdfFileName('');
      } else {
        toast.error('Failed to delete: ' + result.error);
      }
    } catch (error) {
      toast.error('Error deleting fee structure: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Fee Structure</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage fee structure - add tabular entries or upload a PDF
          </p>
        </div>
        <div className="flex gap-2">
          {feeStructure && (
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
            >
              <Trash2 className="w-5 h-5" />
              Delete
            </button>
          )}
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            {feeStructure ? 'Update' : 'Add'} Fee Structure
          </button>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {feeStructure ? 'Update Fee Structure' : 'Add Fee Structure'}
            </h2>
            <button
              onClick={() => setShowForm(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Input Mode Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Input Mode
            </label>
            <div className="flex gap-4">
              <button
                onClick={() => setInputMode('table')}
                className={`flex-1 px-4 py-3 rounded-lg border-2 flex items-center justify-center gap-2 ${
                  inputMode === 'table'
                    ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-blue-600'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                }`}
              >
                <Table className="w-5 h-5" />
                <span>Table Entries</span>
              </button>
              <button
                onClick={() => setInputMode('pdf')}
                className={`flex-1 px-4 py-3 rounded-lg border-2 flex items-center justify-center gap-2 ${
                  inputMode === 'pdf'
                    ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-blue-600'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                }`}
              >
                <FileUp className="w-5 h-5" />
                <span>Upload PDF</span>
              </button>
            </div>
          </div>

          {/* Table Mode */}
          {inputMode === 'table' && (
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100 dark:bg-gray-700">
                      <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left text-sm font-medium">Course/Program</th>
                      <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left text-sm font-medium">Fee</th>
                      <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left text-sm font-medium">Duration</th>
                      <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left text-sm font-medium">Description</th>
                      <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left text-sm font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableEntries.map((entry, index) => (
                      <tr key={index}>
                        <td className="border border-gray-300 dark:border-gray-600 px-3 py-2">
                          <input
                            type="text"
                            value={entry.course}
                            onChange={(e) => handleTableEntryChange(index, 'course', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="e.g., JEE Main"
                          />
                        </td>
                        <td className="border border-gray-300 dark:border-gray-600 px-3 py-2">
                          <input
                            type="text"
                            value={entry.fee}
                            onChange={(e) => handleTableEntryChange(index, 'fee', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="e.g., ₹50,000"
                          />
                        </td>
                        <td className="border border-gray-300 dark:border-gray-600 px-3 py-2">
                          <input
                            type="text"
                            value={entry.duration}
                            onChange={(e) => handleTableEntryChange(index, 'duration', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="e.g., 1 Year"
                          />
                        </td>
                        <td className="border border-gray-300 dark:border-gray-600 px-3 py-2">
                          <input
                            type="text"
                            value={entry.description}
                            onChange={(e) => handleTableEntryChange(index, 'description', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="Optional description"
                          />
                        </td>
                        <td className="border border-gray-300 dark:border-gray-600 px-3 py-2">
                          {tableEntries.length > 1 && (
                            <button
                              onClick={() => handleRemoveRow(index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button
                onClick={handleAddRow}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Row
              </button>
            </div>
          )}

          {/* PDF Mode */}
          {inputMode === 'pdf' && (
            <div className="space-y-4">
              {pdfUrl ? (
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-red-500" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{pdfFileName}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <a
                        href={pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                      <button
                        onClick={() => {
                          setPdfUrl('');
                          setPdfFileName('');
                        }}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                  {uploading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      <span>Upload PDF (Max 10MB)</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        handlePdfUpload(e.target.files[0]);
                      }
                    }}
                    disabled={uploading}
                  />
                </label>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 mt-6">
            <button
              onClick={() => setShowForm(false)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || uploading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Display Current Fee Structure */}
      {feeStructure && !showForm && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Current Fee Structure</h2>
          
          {feeStructure.mode === 'pdf' && feeStructure.pdfUrl ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                <FileText className="w-5 h-5 text-red-500" />
                <span>{feeStructure.pdfFileName || 'Fee Structure PDF'}</span>
              </div>
              <a
                href={feeStructure.pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Download className="w-4 h-4" />
                View/Download PDF
              </a>
            </div>
          ) : feeStructure.entries && feeStructure.entries.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
                <thead>
                  <tr className="bg-gray-100 dark:bg-gray-700">
                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left">Course/Program</th>
                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left">Fee</th>
                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left">Duration</th>
                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {feeStructure.entries.map((entry, index) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-2">{entry.course}</td>
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-2">{entry.fee}</td>
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-2">{entry.duration || '-'}</td>
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-2">{entry.description || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-600 dark:text-gray-400">No fee structure data available.</p>
          )}
        </div>
      )}

      {!feeStructure && !showForm && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
          <p className="text-gray-600 dark:text-gray-400">No fee structure yet. Add your first fee structure!</p>
        </div>
      )}
    </div>
  );
};

export default FeeStructureManagement;

